/**
 * The staff surface's business switcher (ORDR-184).
 *
 * MEASURED BEFORE THIS (at `d98368a`): the staff surface had no business
 * switcher. It acted for whichever firm the session held active, which the
 * gateway stamps as `X-Firm-Id`, and "Acting for" in the framework's
 * `ChameleonShell` names the acting NODE from `/ordering/context`, never a firm.
 * A reseller-admin, a member of the reseller only, had no way here to reach a
 * restaurant they hold a grant on.
 *
 * The framework's `FirmSwitcher` is presentational (it makes no call), so the
 * reads and the switch are this app's (`firmApi.ts`). It lists memberships
 * PLUS ordering reach, a membership winning, labelled by name. A switch posts
 * `/auth/me/active-firm` and then calls `onSwitched`, which re-reads the
 * ordering context exactly as a fresh visit would.
 *
 * ORDR-204: the list is read by the staff door, for the session's user, and
 * handed in (`useBusinesses.ts`), so a change of identity replaces it. A
 * refused switch calls `onRefused`, which re-reads the session: in production
 * the refusal was the first sign that another tab had changed the account.
 *
 * ORDR-207: only ordering businesses are listed (`firmApi.ts` drops a
 * membership in any other tenancy), so a session can hold active a firm this
 * list leaves out. That firm is drawn as "Choose a business", the ORDR-204
 * path, never by its own name and never as "This business".
 *
 * `id="business-switcher"` is the anchor a RESELLER node's pointer links to
 * (ORDR-194): the restaurants it manages are reached here, not in the shell.
 *
 * COURTESY, NOT SECURITY. The server re-validates the chosen firm and scopes
 * every request to the firm the gateway stamps.
 */

import { useCallback, useState, type ReactElement } from "react";
import { FirmSwitcher, type FirmOption } from "@pirouz-ca/ui-components";
import type { ApiClient } from "../api/client.js";
import { switchFirm, type BusinessesResult } from "./firmApi.js";

export interface BusinessSwitcherProps {
  readonly client: ApiClient;
  /** The businesses read for the session's current user; `null` while reading. */
  readonly businesses: BusinessesResult | null;
  /** Read the list again after it failed. */
  readonly onRetry: () => void;
  /** Called once PAIO has accepted the new active firm. */
  readonly onSwitched: () => void;
  /** Called when PAIO refused the switch, so the session can be read again. */
  readonly onRefused: () => void;
}

/**
 * What the framework's `FirmSwitcher` is handed when the session has no active
 * firm. Not a firm id (those are UUIDs), so it can never be posted.
 */
const CHOOSE_A_BUSINESS = "choose-a-business";

/**
 * The options, and the id to draw as selected.
 *
 * NO ACTIVE FIRM IS NOT THE FIRST FIRM (ORDR-204). The framework's
 * `FirmSwitcher` draws `firms.find(active) ?? firms[0]`, so a null active firm
 * read "Business: Demo Ordering Co" in production while onboarding answered 403
 * "no acting firm". A "Choose a business" option is put first and named active,
 * so the component finds it and selects no business. It is added only when
 * there is something to choose.
 *
 * AN UNLISTED ACTIVE FIRM IS NO ACTIVE FIRM HERE (ORDR-207). The list holds
 * ordering businesses only, so a firm active but not listed is, by
 * construction, not one this app acts for: in production it was a firm of
 * another app's tenancy. It takes the same path as a null one, so a null or
 * unknown active firm is never drawn as a real business, nor as the first
 * option's name, which would be a lie about which business the screens below
 * are acting for.
 */
function optionsFor(result: Extract<BusinessesResult, { kind: "ready" }>): { firms: FirmOption[]; activeFirmId: string } {
  const options = result.businesses.map((entry) => ({ id: entry.firmId, label: entry.name, roleLabel: entry.roleLabel }));
  const active = result.activeFirmId;
  if (active === null || !options.some((option) => option.id === active)) {
    return options.length === 0
      ? { firms: options, activeFirmId: CHOOSE_A_BUSINESS }
      : { firms: [{ id: CHOOSE_A_BUSINESS, label: "Choose a business", roleLabel: "" }, ...options], activeFirmId: CHOOSE_A_BUSINESS };
  }
  return { firms: options, activeFirmId: active };
}

export function BusinessSwitcher({
  client,
  businesses,
  onRetry,
  onSwitched,
  onRefused,
}: BusinessSwitcherProps): ReactElement | null {
  const [switching, setSwitching] = useState(false);
  const [switchFailure, setSwitchFailure] = useState<string | null>(null);

  const onSwitch = useCallback(
    (firmId: string): void => {
      if (firmId === CHOOSE_A_BUSINESS) {
        return;
      }
      setSwitching(true);
      setSwitchFailure(null);
      void switchFirm(client, firmId).then((outcome) => {
        setSwitching(false);
        if (outcome.kind === "switched") {
          onSwitched();
        } else {
          setSwitchFailure(outcome.correlationId);
          onRefused();
        }
      });
    },
    [client, onSwitched, onRefused],
  );

  const result = businesses;
  if (result === null) {
    return null;
  }
  if (result.kind === "failed") {
    return (
      <div id="business-switcher" role="alert" data-testid="business-switcher-error" className="flex flex-wrap items-center gap-2 p-2 text-sm">
        <p>We could not load the businesses you can act for.</p>
        <p className="text-[var(--color-neutral-600)]">Reference: {result.correlationId}</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-3 py-1"
        >
          Try again
        </button>
      </div>
    );
  }
  const { firms, activeFirmId } = optionsFor(result);
  return (
    <div id="business-switcher" data-testid="business-switcher" className="flex flex-wrap items-center gap-2 p-2 text-sm">
      <span className="text-[var(--color-neutral-600)]">Business</span>
      <FirmSwitcher firms={firms} activeFirmId={activeFirmId} onSwitch={onSwitch} switching={switching} />
      {switchFailure !== null && (
        <p role="alert" data-testid="business-switch-error" className="text-[var(--color-neutral-600)]">
          We could not change business. Reference: {switchFailure}
        </p>
      )}
    </div>
  );
}
